import { useEffect, useMemo, useState } from 'react';
import { api, type Override, type Permission, type Role, type User, type WorkspaceSummary } from '../../api';
import { useData } from '../../useData';
import { ErrorMsg, Modal } from '../../components/Modal';

type Effect = 'Default' | 'Allow' | 'Deny';
interface Row { effect: Effect; reason: string; validUntil: string }
const key = (permissionId: number, workspaceId: number | null) => `${permissionId}:${workspaceId ?? 'all'}`;

/**
 * Fișa de rol: pentru fiecare permisiune se vede ce dă rolul de bază și se poate alege
 * Implicit (ca rolul) / Permite / Interzice — pentru toată aplicația sau doar într-un workspace.
 */
export function RoleSheet({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: () => void }) {
  const { data: permissions } = useData<Permission[]>('/admin/permissions');
  const { data: roles } = useData<Role[]>('/admin/roles');
  const { data: workspaces } = useData<WorkspaceSummary[]>('/workspaces');
  const { data: existing } = useData<Override[]>(`/admin/users/${user.id}/overrides`);
  const [scopeWs, setScopeWs] = useState<number | null>(null); // null = toată aplicația
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!existing) return;
    const r: Record<string, Row> = {};
    for (const o of existing) {
      r[key(o.permissionId, o.workspaceId)] = {
        effect: o.effect, reason: o.reason ?? '', validUntil: o.validUntil ? o.validUntil.slice(0, 10) : '',
      };
    }
    setRows(r);
  }, [existing]);

  const rolePermIds = useMemo(() => {
    const ids = new Set<number>();
    for (const r of roles ?? []) if (user.roleIds.includes(r.id)) r.permissionIds.forEach((p) => ids.add(p));
    return ids;
  }, [roles, user.roleIds]);

  // Pe un workspace anume arătăm doar permisiunile de workspace
  const shown = (permissions ?? []).filter((p) => scopeWs === null || p.scope === 'Workspace');
  const roleNames = (roles ?? []).filter((r) => user.roleIds.includes(r.id)).map((r) => r.name).join(', ');

  function update(pid: number, patch: Partial<Row>) {
    const k = key(pid, scopeWs);
    setRows((prev) => ({ ...prev, [k]: { ...(prev[k] ?? { effect: 'Default', reason: '', validUntil: '' }), ...patch } }));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const items = (Object.entries(rows) as [string, Row][])
        .filter(([, r]) => r.effect !== 'Default')
        .map(([k, r]) => {
          const [pid, ws] = k.split(':');
          return {
            permissionId: Number(pid),
            workspaceId: ws === 'all' ? null : Number(ws),
            effect: r.effect,
            reason: r.reason || null,
            validUntil: r.validUntil ? new Date(`${r.validUntil}T23:59:59`).toISOString() : null,
          };
        });
      await api.put(`/admin/users/${user.id}/overrides`, items);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const exceptionsCount = (Object.values(rows) as Row[]).filter((r) => r.effect !== 'Default').length;

  return (
    <Modal title={`Fișa de rol: ${user.displayName}`} onClose={onClose} wide>
      <p className="muted">
        Rol de bază: <strong>{roleNames || '—'}</strong>. Aici adăugați drepturi (<em>Permite</em>) sau restricții
        (<em>Interzice</em>) doar pentru acest utilizator. <em>Interzice</em> are întotdeauna prioritate.
      </p>
      <label>Se aplică pentru
        <select value={scopeWs ?? 0} onChange={(e) => setScopeWs(Number(e.target.value) || null)}>
          <option value={0}>Toată aplicația</option>
          {workspaces?.map((w) => <option key={w.id} value={w.id}>Doar în workspace-ul „{w.name}”</option>)}
        </select>
      </label>
      <div className="table-scroll">
        <table>
          <thead><tr><th>Permisiune</th><th>Din rol</th><th>Fișa de rol</th><th>Motiv</th><th>Valabil până la</th></tr></thead>
          <tbody>
            {shown.map((p) => {
              const r = rows[key(p.id, scopeWs)] ?? { effect: 'Default', reason: '', validUntil: '' };
              return (
                <tr key={p.id} className={r.effect === 'Allow' ? 'allow' : r.effect === 'Deny' ? 'deny' : ''}>
                  <td>{p.description}<div className="muted small">{p.code}</div></td>
                  <td>{rolePermIds.has(p.id) ? '✓' : <span className="muted">—</span>}</td>
                  <td>
                    <select value={r.effect} onChange={(e) => update(p.id, { effect: e.target.value as Effect })}>
                      <option value="Default">Implicit</option>
                      <option value="Allow">Permite</option>
                      <option value="Deny">Interzice</option>
                    </select>
                  </td>
                  <td><input disabled={r.effect === 'Default'} value={r.reason} onChange={(e) => update(p.id, { reason: e.target.value })} /></td>
                  <td><input type="date" disabled={r.effect === 'Default'} value={r.validUntil} onChange={(e) => update(p.id, { validUntil: e.target.value })} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ErrorMsg error={error} />
      <div className="actions">
        <span className="muted">{exceptionsCount} excepții în total</span>
        <button onClick={onClose}>Renunță</button>
        <button className="primary" disabled={busy} onClick={save}>Salvează fișa</button>
      </div>
    </Modal>
  );
}
